/**
 * ScoreScaleRowView — presentational half of the 0-10 score scale row
 * (SE-6980, MR-3.1 quick capture).
 *
 * A labelled row of eleven single-select pills built on the SelectableChip
 * molecule (`shape="chip"`), which already owns the selected/unselected class
 * pairing. Nothing here re-spells chip chrome.
 *
 * **Unset is a first-class value, not zero.** The match-report write contract
 * treats an absent score and a score of `0` as different facts: `0` is a real
 * judgement the scout made, absent means they have not judged yet. So `value`
 * is `number | null` and the two states are visually distinct by construction
 * — `value === 0` fills the "0" pill, `value === null` fills none. Any
 * coercion (`value ?? 0`, `value || 0`) anywhere in this file or its callers
 * silently destroys that distinction, which is why the tests assert both
 * states rather than only the selected one.
 *
 * `value` is deliberately wider than the domain (`number`, not the 0-10
 * union): it arrives straight from the API, and an out-of-domain number
 * selects nothing rather than throwing. `onSelect` is narrow, because
 * everything this row can emit is in the domain.
 * @module components/molecules/ScoreScaleRow/ScoreScaleRowView
 */
import React, { memo, type ReactNode } from "react";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import SelectableChip from "@/components/molecules/SelectableChip";

/**
 * The closed score domain. The server rejects anything outside 0-10 rather
 * than clamping it, so the row never offers a value that cannot be stored.
 */
export const SCORE_SCALE_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/** One selectable score. */
export type ScoreScaleValue = (typeof SCORE_SCALE_VALUES)[number];

/** Props for ScoreScaleRowView. */
export interface ScoreScaleRowViewProps {
  /** Row label, e.g. "Performance" or "Potential". */
  readonly label: string;
  /**
   * The selected score, or `null` when nothing has been chosen yet. `0` and
   * `null` are different states and both are renderable.
   */
  readonly value: number | null;
  /**
   * One press handler per score, index-aligned with {@link SCORE_SCALE_VALUES}.
   * The Container binds each handler to its own score so the View stays free
   * of behaviour and no new function is created per render.
   */
  readonly pressHandlers: readonly (() => void)[];
  /**
   * Replaces the rendered label, without replacing {@link ScoreScaleRowViewProps.label}
   * itself (SE-7551).
   *
   * A slot rather than a hint string, so the consumer owns any overlay the
   * label needs — see the note at the render site for why the tooltip cannot
   * live in this file.
   */
  readonly labelSlot?: ReactNode;
  /** Disables every pill in the row. */
  readonly disabled?: boolean;
  /**
   * Draw the compact 32x32 tiles instead of the 48x44 ones.
   *
   * Both sizes are the design's own and neither is a default: frame 32's
   * `Btn/0` is 48x44 for the phone's step-through, frames 12/29's
   * `RatingPill` is 32x32 for the web inline editor. Shrinking the phone's
   * to match would re-open SE-7281 — eleven chips at zero width on device,
   * while web looked correct.
   */
  readonly isCompact?: boolean;
  /**
   * Test identity for the row. Each pill derives its own id by appending the
   * score, e.g. `MATCH_REPORT.PERFORMANCE_SCALE.7`.
   */
  readonly testID?: string;
}

/**
 * Renders the labelled 0-10 scale row.
 * @param root0 - Component props
 * @param root0.label - Row label
 * @param root0.value - Selected score, or null when unset
 * @param root0.pressHandlers - Per-score press handlers from the Container
 * @param root0.labelSlot - Replaces the rendered label, when given
 * @param root0.disabled - Disables every pill
 * @param root0.isCompact - Draw the web editor’s 32x32 tiles rather than the phone’s 48x44
 * @param root0.testID - Test identity for the row
 * @returns The scale row element.
 */
const ScoreScaleRowView = ({
  label,
  value,
  pressHandlers,
  labelSlot,
  disabled = false,
  isCompact = false,
  testID,
}: ScoreScaleRowViewProps) => (
  <Stack space="2" testID={testID}>
    {/*
      The label, or whatever the consumer wants in its place.

      `labelSlot` exists so the TOOLTIP can live in the feature that has
      something to explain, and this file can stay free of it. That is not a
      preference: importing the Gluestack tooltip family here registers
      `cssInterop`, which consumes `className` into `style` for the whole
      rendered tree — measured, the SE-7281 pill-size assertions below started
      reading `undefined` the moment the import was added. Those tests guard a
      real device defect, so the import goes and they keep working.

      `label` is still required and still feeds each pill's accessible name, so
      a slot never costs the row its screen-reader identity.
    */}
    {labelSlot ?? (
      <Text variant="caption" className="text-content-secondary">
        {label}
      </Text>
    )}
    {/*
      A WRAPPING grid of fixed-size tiles, not a single flexed row.

      The eleven scores do not fit across a phone at any sane padding, and the
      previous `flex-1` row did not fail by looking cramped — it failed
      silently. `flex-1` is `flexBasis: 0` on Yoga, so every chip took an equal
      share and the chip's own padding ate the rest: the digits rendered to
      zero width and a scout saw eleven identical empty boxes, unable to tell 0
      from 10 (SE-7281). Web's `min-width: auto` let the content push back,
      which is why it looked right there and why no web check ever caught it.

      Frame 32 settles the shape: 48x44 tiles that never shrink, wrapping to a
      second row. At a phone's interior that is six then five — and the row
      cannot overflow, because a row that would overflow wraps instead.
    */}
    <Box
      className="flex-row flex-wrap gap-1"
      testID={testID ? `${testID}.GRID` : undefined}
    >
      {SCORE_SCALE_VALUES.map((score, index) => (
        <SelectableChip
          key={score}
          label={String(score)}
          isSelected={value === score}
          onPress={pressHandlers[index]}
          shape={isCompact ? "score-compact" : "score"}
          disabled={disabled}
          accessibilityLabel={`${label} ${score}`}
          testID={testID ? `${testID}.${score}` : undefined}
        />
      ))}
    </Box>
  </Stack>
);

ScoreScaleRowView.displayName = "ScoreScaleRowView";

export default memo(ScoreScaleRowView);
